import { router } from "@inertiajs/react";
import { Pencil, Trash2, DollarSign, BookOpen, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/shared/ConfirmProvider";
import AwardStyleBadge from "@/components/schools/rewards/AwardStyleBadge";
import DivisionLeaderList from "@/components/schools/rewards/DivisionLeaderList";
import { AWARD_STYLES, rewardSummary } from "@/lib/prizes";
import { cn } from "@/lib/utils";
import { pluralize } from "@/lib/format";

/**
 * One reward or student prize in a manager list: what it is, how it's
 * earned, how it's going — how close the school or class is (when
 * `showProgress`), or how many students have a prize (when the row has
 * `earned_count`), or which classroom leads each division (top-classroom
 * prizes) — and edit/remove unless the campaign is finished.
 */
export default function RewardRow({ reward, onEdit, locked = false, showProgress = false, removeWarning }) {
  const confirm = useConfirm();
  const styled = reward.award_style && reward.award_style !== "unlock";
  const Icon = styled ? AWARD_STYLES[reward.award_style].icon : reward.kind === "money" ? DollarSign : BookOpen;
  const progress = showProgress && reward.progress_pct != null;

  const remove = async () => {
    const ok = await confirm({
      title: `Remove ${reward.prize}?`,
      body: removeWarning || "It comes off the dashboard, the donation pages and anything you print from now on.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (ok) router.delete(reward.delete_path, { preserveScroll: true });
  };

  return (
    <li className={cn("rounded-lg border bg-card px-4 py-3", progress && reward.unlocked ? "border-success/40" : "border-border")}>
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border",
            progress && reward.unlocked ? "border-success bg-success text-success-foreground" : "border-border bg-secondary text-muted-foreground"
          )}
        >
          {progress && reward.unlocked ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="break-words font-medium text-foreground">{reward.prize}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span>{rewardSummary(reward)}</span>
            <AwardStyleBadge style={reward.award_style} />
            {progress && (reward.unlocked
              ? <span className="font-semibold text-success">Unlocked</span>
              : <span className="font-tabular">· {reward.progress_pct}% of the way</span>)}
            {reward.earned_count != null && <EarnedStatus prize={reward} />}
            {reward.award_style === "top_classroom" && <WonStatus prize={reward} />}
          </div>
          {reward.divisions && <DivisionLeaderList prize={reward} className="mt-2" />}
          {progress && !reward.unlocked && (
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary" aria-hidden="true">
              <div className="h-full rounded-full bg-primary" style={{ width: `${reward.progress_pct}%` }} />
            </div>
          )}
        </div>
        {!locked && (
          <div className="flex shrink-0 items-center gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit} aria-label={`Edit ${reward.prize}`}>
              <Pencil className="h-4 w-4" />
              <span className="hidden sm:inline">Edit</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={remove} aria-label={`Remove ${reward.prize}`} className="text-destructive hover:text-destructive">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}

// A student prize's equivalent of a school reward's progress: how many
// students have it and how many are still waiting to be handed it.
function EarnedStatus({ prize }) {
  const earned = prize.earned_count || 0;
  const waiting = earned - (prize.handed_out_count || 0);
  let text;
  if (prize.award_style === "raffle") {
    text = earned ? `${pluralize(earned, "student")} with entries` : "No entries yet";
    if (prize.draws_count) text += ` · ${pluralize(prize.draws_count, "winner")} drawn`;
  } else if (prize.award_style === "top_in_class") {
    text = earned ? pluralize(earned, "winner") : "Winners are set when the campaign finishes";
  } else {
    text = earned ? `${pluralize(earned, "student")} earned it` : "Nobody yet";
  }

  return (
    <>
      <span className="font-tabular">· {text}</span>
      {waiting > 0 && prize.award_style !== "raffle" && (
        <span className="rounded-full bg-warning/15 px-2 py-0.5 font-medium text-warning-foreground font-tabular">{waiting} to hand out</span>
      )}
    </>
  );
}

// A top-classroom prize's winners so far, once the campaign has finished.
function WonStatus({ prize }) {
  if (!prize.finalized) return null;
  const won = prize.won_count || 0;
  const waiting = won - (prize.handed_out_count || 0);
  return (
    <>
      <span className="font-tabular">· {won ? pluralize(won, "winning class", "winning classes") : "No class qualified"}</span>
      {waiting > 0 && (
        <span className="rounded-full bg-warning/15 px-2 py-0.5 font-medium text-warning-foreground font-tabular">{waiting} to hand out</span>
      )}
    </>
  );
}
